import { ReimbersmentApiHelper } from '../apiHelpers/reimbersmentApiHelper.js';
import { PresignedUrlApiHelper } from '../apiHelpers/presignedUrlApiHelper.js';
import { v4 as uuidv4 } from 'uuid';
import { EntityIdBasedCustomConfig } from "../../config/constants.js"
import loggingService from "../../services/logging.service";
import { LogLevel, StatusCode } from './enums.js';

// format file size bytes in to a readable string
export function formatBytes(bytes, decimals = 2) {
    if (!+bytes) return '0 Bytes'

    const k = 1024
    const dm = decimals < 0 ? 0 : decimals
    const sizes = ['Bytes', 'KiB', 'MiB', 'GiB', 'TiB', 'PiB', 'EiB', 'ZiB', 'YiB']

    const i = Math.floor(Math.log(bytes) / Math.log(k))

    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(dm))} ${sizes[i]}`
}

function splitFilename(filename) {
  const lastDotIndex = filename?.lastIndexOf('.');

  if (lastDotIndex === -1 || lastDotIndex === 0) {
    // No dot found or filename starts with a dot (like .bashrc)
    return { name: filename, extension: '' };
  }

  const name = filename.slice(0, lastDotIndex);
  const extension = filename.slice(lastDotIndex + 1);

  return { name, extension };
}

export const isSafeFilename = (fileName) => {
    if (!fileName || typeof fileName !== "string") return false;
  
    // 1) length check
    if (fileName.length > 200) return false;
  
    // 2) disallowed extensions
    const DISALLOWED_EXT_RE = /\.(?:html?|xhtml|svg|js|mjs|json|php|phtml|asp|aspx|jsp|exe|sh|bat|cmd|scr|msi)$/i;
    if (DISALLOWED_EXT_RE.test(fileName)) return false;
  
    // 3) strict whitelist characters
    const SAFE_FILENAME_RE = /^[A-Za-z0-9_\-\+\s]+(\([A-Za-z0-9_\-\+\s]+\))?\.[A-Za-z0-9]+$/i;
    return SAFE_FILENAME_RE.test(fileName)
      
  }

export class FileUploader {
    static async uploadFileUsingFormData(file, fileName, folderName) {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('folderName', folderName);
        formData.append('fileName', fileName);

        // Call API helper to upload file
        const response = await ReimbersmentApiHelper.uploadUsingFormData(formData);

        return {
            isSuccess: true,
            data: response,
        };

    }
    //
    static async uploadFileData(pmEntityId, file, fileType) {
        let { extension } = splitFilename(file?.name)
        let name = uuidv4().replace(/-/g, '') + '.' + extension.toLowerCase()
        try {
            const uploadResponse = {}
            uploadResponse.isSuccess = false
            uploadResponse.fileName = `${fileType}/${name}`;

            if (EntityIdBasedCustomConfig.rawFileUploadEntityId.includes(parseInt(pmEntityId))) {
                name = 'sunpharma_' + name;
                const { isSuccess } = await FileUploader.uploadFileUsingFormData(file, name, fileType)
                uploadResponse.isSuccess = isSuccess;
                uploadResponse.fileName = `${fileType}/${name}`

                loggingService.log({
                    context: "FileUploader",
                    level: "INFO",
                    method: "POST",
                    message: `Uploading Documents using API frontend pmEntityId : ${pmEntityId}, fileType : ${fileType}, fileName : ${name}`,
                    response_body: uploadResponse,
                    request_body: {
                        pmEntityId,
                        fileType,
                        fileName: name
                    },
                    status_code: uploadResponse.isSuccess ? StatusCode.OK : StatusCode.INTERNAL_SERVER_ERROR,
                })

            } else {
                const response = await FileUploader.uploadFileUsingPresignedUrl(pmEntityId, name, fileType, file)

                uploadResponse.isSuccess = response.isSuccess;
            }

            return uploadResponse

        } catch (error) {
            loggingService.log({
                context: "FileUploader",
                level: "ERROR",
                method: "POST",
                message: `Uploading Documents frontend pmEntityId : ${pmEntityId}, error: ${error.message}`,
                response_body: {isSuccess: false, error: error.message},
                request_body: { pmEntityId, fileType, fileName: name },
                status_code: StatusCode.INTERNAL_SERVER_ERROR,
                error: error.stack
            })
            throw error;

        }

    }

    static async uploadFileUsingPresignedUrl(pmEntityId, fileName, fileType, file) {
        try {
            const request_body = { fileType: fileType, fileName: fileName, entityId: pmEntityId }
            const preSignedUrlResponse = await PresignedUrlApiHelper.generatePresignedUrl(request_body);

            if (preSignedUrlResponse?.data?.presignedGETURL) {
                const response = await PresignedUrlApiHelper.putFileUsingSignedUrl(preSignedUrlResponse.data.presignedGETURL, file);
                return response
            } else {
                return {
                    isSuccess: preSignedUrlResponse.isSuccess, 
                    statusCode: preSignedUrlResponse?.status
                }
            }
        } catch (error) {
            const actualStatusCode = error.response?.status || StatusCode.INTERNAL_SERVER_ERROR;
            loggingService.log({
                context: "FileUploader",
                level: LogLevel.ERROR,
                method: "POST",
                message: `Uploading Documents Presigned Url pmEntityId : ${pmEntityId}, fileType : ${fileType}, fileName : ${fileName}, error: ${error.message}`,
                response_body: {isSuccess: false, error: error.message},
                request_body: { pmEntityId, fileType, fileName },
                status_code: actualStatusCode,
                response_data: error.response?.data,
                error: error.stack
            })
            throw error;
        }
    }
}

export const checkHarmfulPdfContent = (files) => {
  return new Promise((resolve, reject) => {
    const fileArray = Array.from(files);
    const pdfFiles = fileArray.filter((file) => {
      const isPdf =
        file.type === "application/pdf" ||
        file.name.toLowerCase().endsWith(".pdf");
      return isPdf;
    });

    if (pdfFiles.length === 0) {
      resolve(true);
      return;
    }
    const promises = pdfFiles.map((file, index) => {
      return new Promise((resolveFile, rejectFile) => {
        const reader = new FileReader();
        reader.onload = function (e) {
          const arrayBuffer = e.target?.result;
          const arr = new Uint8Array(arrayBuffer);
          let bytes = "";
          for (let i = 0; i < 5; i++) {
            bytes += String.fromCharCode(arr[i]);
          }

          const isValidPdfHeader = bytes === "%PDF-";
          if (!isValidPdfHeader) {
            resolveFile(false);
            return;
          }
          const textDecoder = new TextDecoder("utf-8", { fatal: false });
          const pdfContent = textDecoder.decode(arr);

        const dangerousPatterns = [
          { pattern: /\/JavaScript\s*\(/i, name: "JavaScript action" },
          { pattern: /\/JS\s*\(/i, name: "JS action" },
          { pattern: /\/Sound\b/i, name: "Sound object" },
          { pattern: /\/Movie\b/i, name: "Movie object" },
          { pattern: /\/XFA\b/i, name: "XFA forms" },
        ];


          const detectedThreats = [];
          for (const { pattern, name } of dangerousPatterns) {
            if (pattern.test(pdfContent)) {
              detectedThreats.push(name);
            }
          }

          if (detectedThreats.length > 0) {
            resolveFile(false);
            return;
          }
          resolveFile(true);
        };

        reader.onerror = function () {
          rejectFile(reader.error);
        };
        reader.readAsArrayBuffer(file);
      });
    });

    Promise.all(promises)
      .then((results) => {
        const allValid = results.every((isValid) => isValid === true);
        resolve(allValid);
      })
      .catch((error) => {
        console.error("PDF validation error:", error);
        reject(error);
      });
  });
};
